/**
 * PWA install hook + iOS detection.
 * Captures the `beforeinstallprompt` event globally so the Profile page
 * can offer an install button on Android/Desktop Chrome.
 */
import { useEffect, useState, useCallback } from "react";

let _deferredPrompt = null;
const _listeners = new Set();

function _emit() {
  for (const fn of _listeners) fn(_deferredPrompt);
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    _deferredPrompt = e;
    _emit();
  });
  window.addEventListener("appinstalled", () => {
    _deferredPrompt = null;
    _emit();
  });
}

export function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/i.test(ua) && !window.MSStream;
}

export function iOSVersion() {
  if (!isIOS()) return null;
  const m = navigator.userAgent.match(/OS (\d+)_(\d+)/);
  if (!m) return null;
  return { major: parseInt(m[1], 10), minor: parseInt(m[2], 10) };
}

export function useInstallPrompt() {
  const [prompt, setPrompt] = useState(_deferredPrompt);
  const [installed, setInstalled] = useState(isStandalone());

  useEffect(() => {
    const cb = (p) => {
      setPrompt(p);
      setInstalled(isStandalone());
    };
    _listeners.add(cb);
    return () => _listeners.delete(cb);
  }, []);

  const trigger = useCallback(async () => {
    if (!prompt) return null;
    prompt.prompt();
    const choice = await prompt.userChoice;
    if (choice?.outcome === "accepted") {
      _deferredPrompt = null;
      setPrompt(null);
    }
    return choice?.outcome || null;
  }, [prompt]);

  return { canPrompt: Boolean(prompt), trigger, installed, ios: isIOS(), iosVersion: iOSVersion() };
}
